// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xngGaPLPEvent from "@guestsupply/App/Handlers/GoogleAnalytics/PLP/PLPEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { XProductFilters } from "@guestsupply/XDL/types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}
interface BrandStandardsDataModel {
    BrandStandardCode: string;
    BrandStandardName: string;
    IsSelected: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        brandStandardsFacets: getProductListDataViewProperty(state, "brandStandards" as any),
        brandStandards: (state.pages.productList.productFilters as XProductFilters).brandStandards,
        isAuthenticated: xIsAuthenticated(state),
        productsDataView: getProductListDataView(state),
        brandStandardsArray: (getProductListDataView(state) as any)?.properties?.brandStandards,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    // xng-change: add google event
    xngGaPLPEvent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const ProductListsBrandStandardsFilterStyles: ProductListFilterAccordionSectionStyles = {};

const ProductListsBrandStandardsFilter: FC<Props> = ({
    addProductFilters,
    removeProductFilters,
    fields,
    brandStandards,
    isAuthenticated,
    brandStandardsArray,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaPLPEvent,
}) => {
    if (!isAuthenticated || !brandStandardsArray) {
        return null;
    }

    // added here because  JSON.parse would give an error when paring undefined
    const brandStandardsData = JSON.parse(brandStandardsArray) as BrandStandardsDataModel[];

    const onChangeBrandStandardFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ brandStandards: [facet.id] } as XProductFilters);
        } else {
            addProductFilters({ brandStandards: [facet.id] } as XProductFilters);
        }
        // xng-change: add google event
        xngGaPLPEvent({
            plpEventType: GOOGLE_EVENTS.PLP_FILTER,
            includePLPEvent: xGoogleAnalytics.includePLPFilterEvent,
            clickText: facet.name,
            attribute: translate("Brand Standards"),
        });
    };

    const facets = brandStandardsData.map(brand => {
        return {
            attributeValueId: brand.BrandStandardCode,
            count: -1,
            selected: (brand.IsSelected || brandStandards?.includes(brand.BrandStandardCode)) ?? false,
            value: brand.BrandStandardName,
            valueDisplay: brand.BrandStandardName,
            id: brand.BrandStandardCode,
            name: brand.BrandStandardName,
        };
    });

    return (
        <>
            <ProductListFiltersAccordionSection
                title={translate("Brand Standards")}
                facets={facets}
                onChangeFacet={onChangeBrandStandardFacet}
                showMoreLimit={2}
                expandByDefault={fields.expandByDefault}
                extendedStyles={productListCategoryFiltersStyles}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListsBrandStandardsFilter),
    definition: {
        group: "Product List",
        displayName: "Brand Standards Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
